import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, Plus, Wrench, AlertTriangle, X, CheckCircle2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  getInstrumentCalibrations,
  getOverdueInstrumentCalibrations,
  createInstrumentCalibration,
} from '../../services/api'

const KIND_LABELS = {
  multispec_panel: 'Multispectral Cal Panel',
  hanging_scale: 'Hanging Scale',
  gps_device: 'GPS Device',
  quadrat_frame: 'Quadrat Frame',
  soil_probe: 'Soil Probe',
  thermometer: 'Thermometer',
  moisture_meter: 'Moisture Meter',
  other: 'Other',
}

export default function InstrumentCalibrations({ ranchId }) {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)

  const { data: rows, isLoading } = useQuery({
    queryKey: ['instrumentCalibrations', ranchId],
    queryFn: () => getInstrumentCalibrations(ranchId),
  })

  const { data: overdue } = useQuery({
    queryKey: ['instrumentCalibrationsOverdue', ranchId],
    queryFn: () => getOverdueInstrumentCalibrations(ranchId),
  })

  const today = new Date().toISOString().slice(0, 10)
  const isOverdue = (r) => r.next_due_date && r.next_due_date < today

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Instrument Calibrations</h1>
            <p className="text-gray-500 mt-1">
              Cal panels, scales, GPS handhelds, quadrat frames — calibration trail per drone-operator §4
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" /> Record Calibration
        </button>
      </div>

      {overdue && overdue.length > 0 && (
        <div className="card p-4 bg-amber-50 border-amber-200 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-amber-900">
              {overdue.length} instrument{overdue.length === 1 ? '' : 's'} overdue
            </div>
            <ul className="text-sm text-amber-800 mt-1 list-disc list-inside">
              {overdue.map(r => (
                <li key={r.id}>
                  {KIND_LABELS[r.instrument_kind]}{r.instrument_label ? ` — ${r.instrument_label}` : ''}
                  {' '}due {r.next_due_date}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : !rows?.length ? (
          <div className="p-8 text-center text-gray-400">
            No instrument calibrations recorded yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Instrument</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Label / Serial</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calibrated</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.id} className={isOverdue(r) ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-gray-400" />
                      {KIND_LABELS[r.instrument_kind] || r.instrument_kind}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {r.instrument_label || '—'}
                    {r.serial_number ? ` (${r.serial_number})` : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {r.calibration_date ? format(parseISO(r.calibration_date), 'dd MMM yyyy') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.next_due_date ? (
                      <span className={isOverdue(r) ? 'text-amber-700 font-medium' : 'text-gray-700'}>
                        {format(parseISO(r.next_due_date), 'dd MMM yyyy')}
                        {isOverdue(r) && ' (overdue)'}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{r.calibrated_by || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateModal
          ranchId={ranchId}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['instrumentCalibrations'] })
            queryClient.invalidateQueries({ queryKey: ['instrumentCalibrationsOverdue'] })
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}

function CreateModal({ ranchId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    instrument_kind: 'multispec_panel',
    instrument_label: '',
    serial_number: '',
    calibration_date: new Date().toISOString().slice(0, 10),
    next_due_date: '',
    calibrated_by: '',
    notes: '',
  })
  const [error, setError] = useState(null)
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const mutation = useMutation({
    mutationFn: () => createInstrumentCalibration({
      ranch_id: ranchId,
      instrument_kind: form.instrument_kind,
      instrument_label: form.instrument_label || null,
      serial_number: form.serial_number || null,
      calibration_date: form.calibration_date,
      next_due_date: form.next_due_date || null,
      calibrated_by: form.calibrated_by || null,
      notes: form.notes || null,
    }),
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save calibration'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Wrench className="h-5 w-5 text-green-600" /> Record Calibration
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate() }}
          className="p-4 space-y-4"
        >
          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <div>
            <label className="label">Instrument *</label>
            <select className="input w-full"
              value={form.instrument_kind} onChange={e => update('instrument_kind', e.target.value)}>
              {Object.entries(KIND_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Label</label>
              <input className="input w-full" value={form.instrument_label}
                onChange={e => update('instrument_label', e.target.value)}
                placeholder="Cal Panel A" />
            </div>
            <div>
              <label className="label">Serial</label>
              <input className="input w-full" value={form.serial_number}
                onChange={e => update('serial_number', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Calibration Date *</label>
              <input type="date" className="input w-full" value={form.calibration_date}
                onChange={e => update('calibration_date', e.target.value)} required />
            </div>
            <div>
              <label className="label">Next Due Date</label>
              <input type="date" className="input w-full" value={form.next_due_date}
                onChange={e => update('next_due_date', e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">Calibrated By</label>
            <input className="input w-full" value={form.calibrated_by}
              onChange={e => update('calibrated_by', e.target.value)}
              placeholder="Lab name or technician" />
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea className="input w-full" rows={2} value={form.notes}
              onChange={e => update('notes', e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2">
              {mutation.isPending ? 'Saving…' : (<><CheckCircle2 className="h-4 w-4" /> Save</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
